import { useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  useMotionTemplate,
} from "framer-motion";
import { Lightbulb, MonitorSmartphone, Smartphone, Rocket } from "lucide-react";

const STAGES = [
  {
    icon: Lightbulb,
    num: "01",
    tag: "IDEA",
    title: "The Spark",
    text: "Every build starts with your idea. We shape it into a strategy that can actually win in your market.",
  },
  {
    icon: MonitorSmartphone,
    num: "02",
    tag: "BUILD",
    title: "The Website",
    text: "Design and engineering turn that spark into a fast, conversion-first website that looks world-class.",
  },
  {
    icon: Smartphone,
    num: "03",
    tag: "EXTEND",
    title: "The App",
    text: "We extend your platform to iOS and Android — your business in every pocket, always one tap away.",
  },
  {
    icon: Rocket,
    num: "04",
    tag: "IGNITE",
    title: "The Growth",
    text: "Marketing ignition: SEO, paid campaigns and WhatsApp funnels pour fuel on the fire.",
  },
];

const FADES = [
  [[0, 0.2, 0.27], [1, 1, 0]],
  [[0.22, 0.29, 0.45, 0.52], [0, 1, 1, 0]],
  [[0.47, 0.54, 0.7, 0.77], [0, 1, 1, 0]],
  [[0.72, 0.79, 1], [0, 1, 1]],
];

const StageIcon = ({ icon: Icon, index, progress }) => {
  const [input, output] = FADES[index];
  const opacity = useTransform(progress, input, output);
  const scale = useTransform(progress, input, output.map((o) => 0.6 + o * 0.4));
  return (
    <motion.div style={{ opacity, scale }} className="absolute inset-0 flex items-center justify-center">
      <Icon className="h-10 w-10 text-[#D9A92E]" strokeWidth={1.5} />
    </motion.div>
  );
};

const StageCaption = ({ stage, index, progress }) => {
  const [input, output] = FADES[index];
  const opacity = useTransform(progress, input, output);
  const y = useTransform(progress, input, output.map((o, i) => (i === 0 && index !== 0 ? 40 : o === 1 ? 0 : -40)));
  return (
    <motion.div style={{ opacity, y }} className="absolute inset-0" data-testid={`journey-stage-${stage.num}`}>
      <span className="font-display text-5xl font-extrabold text-[#D9A92E]/25 lg:text-7xl">{stage.num}</span>
      <h3 className="font-display mt-2 text-2xl font-bold text-white sm:text-3xl">{stage.title}</h3>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-[#A1A1AA] sm:text-base">{stage.text}</p>
    </motion.div>
  );
};

const Journey = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(3, Math.max(0, Math.floor(v * 4))));
  });

  const roadY = useTransform(scrollYProgress, [0, 1], [0, 1400]);
  const roadPos = useMotionTemplate`0px ${roadY}px, 0px 0px, center ${roadY}px`;
  const travelX = useTransform(scrollYProgress, [0, 1], ["-34vw", "32vw"]);
  const travelY = useTransform(scrollYProgress, [0, 1], ["7vh", "-3vh"]);
  const travelScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.6, 0.95, 1.45]);
  const glow = useTransform(scrollYProgress, [0, 1], [0.25, 0.75]);
  const barScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section ref={ref} className="relative h-[340vh] bg-[#09090B]" data-testid="journey-section">
      <div className="sticky top-0 flex h-screen flex-col overflow-hidden">
        <div className="grid-lines absolute inset-0 opacity-60" />
        <div className="relative z-20 mx-auto w-full max-w-7xl px-4 pt-28 sm:px-8 lg:px-12">
          <p className="mono text-xs uppercase tracking-[0.2em] text-[#E9C766] sm:text-sm">The journey</p>
          <h2 className="font-display mt-3 text-xl font-bold tracking-tight text-white sm:text-2xl lg:text-3xl">
            From <span className="text-[#D9A92E]">spark</span> to scale.
          </h2>
        </div>

        <div className="absolute inset-x-0 top-[46%] z-0 h-px bg-gradient-to-r from-transparent via-[#D9A92E]/60 to-transparent" />
        <div className="absolute inset-x-0 top-[36%] z-0 h-48 bg-[#D9A92E]/15 blur-[110px]" />

        <div className="absolute inset-x-0 bottom-0 top-[46%] z-0 overflow-hidden" style={{ perspective: "1000px" }}>
          <motion.div
            className="absolute inset-x-[-60%] top-0 h-[280%]"
            style={{
              transform: "rotateX(64deg)",
              transformOrigin: "top center",
              backgroundPosition: roadPos,
              backgroundImage:
                "repeating-linear-gradient(0deg, transparent 0px, transparent 48px, rgba(217,169,46,0.75) 48px, rgba(217,169,46,0.75) 52px), repeating-linear-gradient(90deg, transparent 0px, transparent 124px, rgba(120,150,220,0.16) 124px, rgba(120,150,220,0.16) 128px), repeating-linear-gradient(0deg, rgba(217,169,46,1) 0px, rgba(217,169,46,1) 34px, transparent 34px, transparent 76px)",
              backgroundSize: "auto, auto, 7px auto",
              backgroundColor: "rgba(11,20,55,0.03)",
              backgroundRepeat: "repeat, repeat, repeat-y",
            }}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#09090B] via-transparent to-[#09090B]" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#09090B] to-transparent" />
        </div>

        <motion.div
          className="absolute left-1/2 top-[52%] z-10"
          style={{ x: travelX, y: travelY, scale: travelScale }}
        >
          <motion.div
            className="absolute right-[60%] top-1/2 h-10 w-64 -translate-y-1/2 rounded-full bg-gradient-to-l from-[#D9A92E]/50 to-transparent blur-xl"
            style={{ opacity: glow }}
          />
          <div className="relative h-24 w-24 -translate-x-1/2 rounded-2xl border border-[#D9A92E]/50 bg-[#09090B]/90 shadow-[0_0_60px_rgba(217,169,46,0.35)] backdrop-blur-md">
            {STAGES.map((s, i) => (
              <StageIcon key={s.tag} icon={s.icon} index={i} progress={scrollYProgress} />
            ))}
          </div>
        </motion.div>

        <div className="absolute bottom-20 left-4 right-4 z-20 sm:left-8 lg:left-12">
          <div className="relative h-44 max-w-lg">
            {STAGES.map((s, i) => (
              <StageCaption key={s.num} stage={s} index={i} progress={scrollYProgress} />
            ))}
          </div>
        </div>

        <div className="absolute bottom-24 right-4 z-20 hidden sm:right-8 sm:block lg:right-12">
          <p className="mono mb-3 text-right text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
            {STAGES[active].tag} — 0{active + 1}/04
          </p>
          <div className="flex gap-2">
            {STAGES.map((s, i) => (
              <span
                key={s.num}
                className={`h-1 w-10 rounded-full transition-colors duration-500 ${
                  i <= active ? "bg-[#D9A92E]" : "bg-white/15"
                }`}
              />
            ))}
          </div>
        </div>

        <motion.div
          className="absolute bottom-0 left-0 z-20 h-[3px] w-full origin-left bg-gradient-to-r from-[#D9A92E] to-[#E8C05A]"
          style={{ scaleX: barScale }}
        />
      </div>
    </section>
  );
};

export default Journey;
